import { Injector } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { DropDownListComponent } from "@progress/kendo-angular-dropdowns";
import { debounceTime, filter, take } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import { DateUtil } from "../date-util/date-util";
import { FormBase } from "../form-base/form-base";
import { PolluxFormConfig } from "../pollux-form-config/pollux-form-config";
import { WorkspaceBase } from "../workspace-base/workspace-base";

/**
 * Standardized main Form base class. Use this to implement the main forms used for Business Objects (BOs)
 */
export abstract class MainFormBase<T> extends FormBase<T> {
  /**
   * Gets the workspace reference
   */
  public abstract get workspaceReference(): WorkspaceBase;

  /**
   * Gets the form configuration
   */
  protected abstract get formConfig(): PolluxFormConfig[];

  /**
   * Read-only flag
   */
  private formReadOnly: boolean;

  /**
   * Format of dates on form
   */
  protected get dateFormat(): string {
    return 'dd/MM/yyyy';
  }

  /**
   * Format of dates with time on form
   */
  protected get dateTimeFormat(): string {
    return 'dd/MM/yyyy HH:mm';
  }

  /**
   * The current OID of the BO to be loaded. Please provide an @Input setter for this like followind:
   
   @Input('oid') set oid(value: number) {
    this.currentOid = value;
   }

   */
  public currentOid: number;

  /**
   * If the OID of the BO to be loaded is <= 0, we're in new mode
   */
  public get isNew(): boolean {
    return this.currentOid <= 0;
  }

  /**
   * Gets the API GET endpoint for loading a single BO by it's OID
   * @param oid The BO's OID
   */
  protected abstract getLoadSingleEndpoint(oid: any): string;

  /**
  * Gets the API POST endpoint for saving a single BO
  */
  protected abstract getSaveSingleEndpoint(): string;

  /**
   * Constructor
   * @param injector The Angular injector 
   */
  constructor(protected injector: Injector) {
    super(injector);
  }

  /**
   * Loads the data
   */
  protected loadData(): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const url = this.getLoadSingleEndpoint(this.currentOid);
      this.httpClient.get(url).pipe(take(1)).subscribe({
        next: (data: any) => { resolve(data); },
        error: (error: any) => { reject(error); }
      });
    });
  }

  /**
   * Saves the data. Additional steps like validation, user interaction etc can also be performed here.
   * The implementation should callt he API endpoint returned by this.getSaveSingleEndpoint(), but can also return EMPTY
   * if nothing should be done.
   * @param data The data to be saved
   */
  protected saveData(data: T): Promise<any> {
    const url = this.getSaveSingleEndpoint();

    return new Promise<any>((resolve, reject) => {
      this.httpClient.post(url, data).pipe(take(1)).subscribe({
        next: (data: any) => { resolve(data); },
        error: (error: any) => { reject(error); }
      });
    });
  }

  /**
   * Sets the form read-only or not
   * @param formReadOnly Read-only flag
   */
  public setReadOnly(formReadOnly: boolean): void {
    this.formReadOnly = formReadOnly;
    this.checkReadOnly();
  }

  /**
  * Gets the data that should be saved. Please don't do any date parsing/formatting here.
  * Typically, you'll just want to return the rawData parameter directly.
  * @param _form The FormGroup
  * @param rawData The FormGroup's raw data
  */
  protected getSaveData(_form: UntypedFormGroup, rawData: any) {
    return rawData;
  }

  /**
   * Creates the FormGroup and populates it with the data.
   * Important: Please do not parse any Dates from string in this method!
   * @param data Loaded and post-processed data
   */
  protected createFormGroup(data: any): UntypedFormGroup {
    const form = this.buildFormFromConfig(data, false);

    return form;
  }

  /**
   * Updates the retrievers
   */
  protected updateRetrievers(): void {
    this.setDefaultRetrieverData(this.formData.getRawValue());
  }

  /**
   * Register a retriever component
   * @param retriever The retriever component
   * @param area The area
   */
  protected registerRetrieverComponent(retriever: DropDownListComponent, area: string) {
    this.subscriptions.add(retriever.filterChange
      .pipe(
        //skip empty string search: retriever emits empty value when something is selected in dropdown
        filter((value) => value?.length > 0),
        debounceTime(500)
      )
      .subscribe((value) => {
        retriever.loading = true;
        this.loadRetriever(area, value).then((data) => {
          this.retrieverData[area] = data.results;
          this.setDefaultRetrieverData(this.formData.getRawValue());
          retriever.loading = false;
        }).catch((_error) => {
          alert('Failed to set default retriever data!');
        });
      }));
  }

  /**
   * Checks if a field should be disabled. If it should be disabled, true is returned.
   * @param field The field config
   * @param data The data
   * @param formReadOnly The form read-only flag
   * @returns true or false
   */
  private shouldDisable(field: PolluxFormConfig, formReadOnly: boolean): boolean {
    if (field.keepEnabled) {
      return false;
    }
    if (field.disabled) {
      return true;
    }
    if (field.disabledEvaluators?.length) {
      for (const disabledEvaluator of field.disabledEvaluators) {
        if (disabledEvaluator(this.formData, formReadOnly)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Builds the FormGroup from the config
   * @param data The data
   * @param formReadOnly Form read-only flag
   * @returns The FormGroup
   */
  protected buildFormFromConfig(data: any, formReadOnly: boolean): UntypedFormGroup {
    const form = new UntypedFormGroup({});

    //not all fields of DTO might be in config, so track which ones are not so we can add them to form group
    //we are doing this because form group values are posted to server and our DB proc expects whole DTO
    const missingDataKeys = Object.keys(data);//start with all data fields

    for (const field of this.formConfig) {
      let control: UntypedFormControl;
      if (field.fixedValue) {
        control = new UntypedFormControl(field.fixedValue);
      }
      else {
        control = new UntypedFormControl(data[field.name]);
      }

      this.applyValidators(field, control);

      this.applyDisabledState(field, formReadOnly, control);

      form.addControl(field.name, control);

      //if field is in config remove it from missingDataKeys
      const idx = missingDataKeys.indexOf(field.name);
      if (idx !== -1) {
        missingDataKeys.splice(idx, 1);
      }
    }

    //at the end iterate over missing keys and add them to form group as disabled controls. If they are not added to HTML, they will still be there, so no data loss can occur
    this.addMissingKeys(missingDataKeys, data, form);

    this.subscriptions.add(form.valueChanges.subscribe(() => {
      this.checkReadOnly();
    }));

    return form;
  }

  /**
   * Applies the disabled state and makes fields read-only or editable
   * @param field Field config
   * @param formReadOnly Read-only flag
   * @param control The field's control
   */
  private applyDisabledState(field: PolluxFormConfig, formReadOnly: boolean, control: UntypedFormControl): void {
    if (this.shouldDisable(field, formReadOnly)) {
      control.disable({ emitEvent: false });
    }
    else {
      control.enable({ emitEvent: false });
    }
  }

  /**
   * Applies all validators for a field
   * @param field Field config
   * @param control The field's control
   */
  private applyValidators(field: PolluxFormConfig, control: UntypedFormControl): void {
    let validators = [];
    if (field.validators) {
      validators = field.validators;
    }

    if (field.formReferencingValidators) {
      for (const validatorRegistration of field.formReferencingValidators) {
        const validator = validatorRegistration(this);
        validators.push(validator);
      }
    }
    if (validators.length) {
      control.setValidators(validators);
    }
  }

  /**
   * Adds fields defined in the DTO, but missing in the form definition, to the form.
   * If this method isn't called, the DTO sent at saving only contains the fields defined in the form definition.
   * If these are then saved back to the database, data loss would occur.
   * @param missingDataKeys Missing data keys (names)
   * @param data Form data
   * @param form The form
   */
  private addMissingKeys(missingDataKeys: string[], data: any, form: UntypedFormGroup): void {
    if (!environment.production && missingDataKeys.length > 0) {
      //In dev, warn people that entries are missing. They can add stuff to the form definitions for clarity, but it's not mandatory
      console.log('Adding missing keys to form group', missingDataKeys);
    }

    for (const key of missingDataKeys) {
      const value = data[key];
      const control = new UntypedFormControl(value);
      control.disable({ emitEvent: false });
      form.addControl(key, control);
    }
  }

  /**
   * Checks if fields should be set read-only or not and updates thir state
   */
  protected checkReadOnly() {
    for (const field of this.formConfig) {
      const control = this.formData.controls[field.name];
      if (this.shouldDisable(field, this.formReadOnly)) {
        control.disable({ emitEvent: false });
      }
      else {
        control.enable({ emitEvent: false });
      }
    }
  }

  /**
   * Important: All date values are treated as UTC dates in the backend.
   * Therefore, please parse all date values into a Date by using DateUtil.getDate within this method.
   * This step occurs directly before the createFormGroup step
   * @param data The loaded and post-processed data
   */
  protected parseDatesOnLoad(data: any): void {
    for (const field of this.formConfig) {
      if (field.isDate) {
        data[field.name] = DateUtil.getDate(data[field.name]);
      }
    }
  }

  /**
   * Important: All date values are treated as UTC dates in the backend.
   * However, the UI works with TypeScript Dateobjects, which are in the user's local time zone.
   * Therefore, please parse all date values into a UTC ISO strings by using DateUtil.dateToUtcString within this method.
   * This step occurs directly before the saveData step
   * @param data The data that should be saved
   */
  protected formatDatesOnSave(data: any): void {
    for (const field of this.formConfig) {
      if (field.isDate) {
        data[field.name] = DateUtil.dateToUtcString(data[field.name]);
      }
    }
  }
}
